import Phaser from 'phaser';

/**
 * **Os planetas, desenhados: placas encaixadas, fendas que abrem, núcleos que brilham.**
 *
 * ## Porque não é a arte dos cometas
 *
 * Porque tem de se distinguir de longe. Um cometa é um punhado de seixos
 * escuros; um planeta é um corpo maior feito de **placas** que encaixam, com uma
 * luz interna que cresce à medida que o exterior desaparece. As duas silhuetas
 * partilham a disciplina técnica — texturas geradas uma vez, piscinas com teto,
 * identidades autoritativas como chaves — e nada da forma.
 *
 * ## O que ele nunca decide
 *
 * Vida, exposição, recompensa ou tempo. Cada placa desenhada vem da máscara
 * autoritativa; cada núcleo vem da lista de EXPOSTOS que a trama trouxe; cada
 * fragmento e cada brilho vem de um evento que a sala já produziu.
 *
 * O ouro de uma placa só aparece quando o evento `gold` chega — e esse evento é
 * publicado depois de o crédito ter acontecido. Antes disso não há por onde o
 * desenhar, o que é o que impede um jogador de escolher por onde começar.
 *
 * ## O núcleo é mineral, e não é o item
 *
 * Um corpo preciso com halo e pulsação lenta, e mais nada: sem nome, sem
 * raridade, sem moldura e sem ícone. A arte final de um item não existe, e
 * inventar uma aqui seria prometer uma coisa que o jogador veria desmentida no
 * inventário.
 *
 * ## Pendente de avaliação
 *
 * A aparência **não está aprovada**. A inspeção visual do projeto acontece no
 * encerramento, e nenhuma alegação de desempenho é feita aqui: nada foi medido
 * num navegador.
 */

const TEXTURE = {
  plate: 'open-world-planet-plate',
  crack: 'open-world-planet-crack',
  core: 'open-world-planet-core',
  dust: 'open-world-planet-dust',
} as const;

const PLATE_PX = 96;
const CRACK_PX = 128;
const CORE_PX = 64;
const DUST_PX = 16;

/** O teto dos detritos vivos. O primeiro que o LOD corta. */
const DEBRIS_MAX = 48;
/** O teto da poeira. Corta antes dos detritos: é o mais decorativo dos dois. */
const DUST_MAX = 32;

const DEBRIS_LIFE_MS = 520;
const DUST_LIFE_MS = 700;
const FLASH_LIFE_MS = 200;

/** As cores das placas. Terra escura, tingida a partir de uma textura branca. */
const PLATE_TINTS = [0x5a4f45, 0x4a423c, 0x655a4e, 0x453d38] as const;
/** A luz interna, que cresce com as fendas. */
const CRACK_TINT = 0xff8a3d;
/** O ouro, no instante em que uma placa o larga. */
const GOLD_TINT = 0xffd24a;
/** O núcleo mineral. Corpo quase branco, halo frio. */
const CORE_TINT = 0xf4faff;
const CORE_HALO_TINT = 0x7ad2ff;

function hash(a: number, b: number): number {
  let value = (a * 0x1f1f_1f1f) ^ (b * 0x8da6_b343);
  value = Math.imul(value ^ (value >>> 16), 0x21f0_aaad);
  value = Math.imul(value ^ (value >>> 15), 0x735a_2d97);
  return (value ^ (value >>> 15)) >>> 0;
}

function unit(a: number, b: number): number {
  return hash(a, b) / 0x1_0000_0000;
}

interface PlateGraphics {
  fillStyle(color: number, alpha?: number): PlateGraphics;
  fillCircle(x: number, y: number, radius: number): PlateGraphics;
  fillRect(x: number, y: number, width: number, height: number): PlateGraphics;
  fillTriangle(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ): PlateGraphics;
  generateTexture(key: string, width?: number, height?: number): unknown;
  destroy(): void;
}

interface TextureScene {
  readonly textures: { exists(key: string): boolean };
  readonly make: {
    graphics(config: { x: number; y: number }, addToScene: boolean): PlateGraphics;
  };
}

/**
 * **A placa**: um hexágono achatado, branco para ser tingido.
 *
 * Encaixa com as vizinhas quando o corpo está inteiro, e a ausência de uma
 * abre um buraco de forma óbvia — que é o que "perder partes altera claramente
 * a silhueta" tem de significar sem arte final.
 */
function makePlateTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.plate)) {
    return;
  }
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  const c = PLATE_PX / 2;
  const r = c * 0.94;
  g.fillStyle(0xff_ff_ff, 1);
  /* Seis triângulos a partir do centro: um hexágono sem uma malha. */
  for (let index = 0; index < 6; index += 1) {
    const a0 = (index / 6) * Math.PI * 2;
    const a1 = ((index + 1) / 6) * Math.PI * 2;
    g.fillTriangle(
      c,
      c,
      c + Math.cos(a0) * r,
      c + Math.sin(a0) * r,
      c + Math.cos(a1) * r,
      c + Math.sin(a1) * r,
    );
  }
  g.generateTexture(TEXTURE.plate, PLATE_PX, PLATE_PX);
  g.destroy();
}

/**
 * **As fendas**: um disco de luz que vive POR BAIXO das placas.
 *
 * Quanto menos placas houver, mais dele se vê. Não é um efeito de ecrã e não é
 * um shader: é um sprite tingido cuja opacidade sobe com o dano do corpo.
 */
function makeCrackTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.crack)) {
    return;
  }
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  const c = CRACK_PX / 2;
  for (let step = 10; step > 0; step -= 1) {
    g.fillStyle(0xff_ff_ff, 0.07);
    g.fillCircle(c, c, (c * step) / 10);
  }
  g.generateTexture(TEXTURE.crack, CRACK_PX, CRACK_PX);
  g.destroy();
}

/** O núcleo mineral: corpo preciso, halo suave. Nada que se leia como um item. */
function makeCoreTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.core)) {
    return;
  }
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  const c = CORE_PX / 2;
  for (let step = 8; step > 0; step -= 1) {
    g.fillStyle(0xff_ff_ff, 0.09);
    g.fillCircle(c, c, (c * step) / 8);
  }
  /* Um cristal: quatro triângulos a formar um losango. */
  g.fillStyle(0xff_ff_ff, 1);
  const r = c * 0.34;
  g.fillTriangle(c, c - r, c - r * 0.6, c, c + r * 0.6, c);
  g.fillTriangle(c, c + r, c - r * 0.6, c, c + r * 0.6, c);
  g.generateTexture(TEXTURE.core, CORE_PX, CORE_PX);
  g.destroy();
}

/** Um grão de poeira. O mais barato de tudo, e o primeiro a ser cortado. */
function makeDustTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.dust)) {
    return;
  }
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  const c = DUST_PX / 2;
  g.fillStyle(0xff_ff_ff, 1);
  g.fillCircle(c, c, c * 0.6);
  g.generateTexture(TEXTURE.dust, DUST_PX, DUST_PX);
  g.destroy();
}

interface Mote {
  readonly sprite: Phaser.GameObjects.Image;
  x: number;
  y: number;
  vx: number;
  vy: number;
  bornAtMs: number;
  life: number;
}

/** O que a cena entrega para desenhar um planeta. Só o que a autoridade disse. */
export interface PlanetDrawState {
  readonly id: string;
  readonly generation: number;
  readonly x: number;
  readonly y: number;
  readonly radiusMu: number;
  readonly parts: number;
  readonly visualSeed: number;
  readonly mask: number;
  readonly cores: readonly { readonly index: number; readonly health: number }[];
}

/** Um evento a desenhar uma vez. */
export interface PlanetDrawEvent {
  readonly id: string;
  readonly ordinal: number;
  readonly kind: 'impact' | 'fragment' | 'gold' | 'reveal' | 'acquired' | 'full' | 'rebuilt';
  readonly part: number;
  readonly core: number;
  readonly x: number;
  readonly y: number;
}

export class OpenWorldPlanetArt {
  private readonly plates = new Map<string, Phaser.GameObjects.Image>();
  private readonly cracks = new Map<string, Phaser.GameObjects.Image>();
  private readonly cores = new Map<string, Phaser.GameObjects.Image>();
  private readonly debris: Mote[] = [];
  private readonly dust: Mote[] = [];
  private readonly flashes: Mote[] = [];
  private readonly seenEvent = new Map<string, number>();

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly depth: number,
  ) {
    const target = scene as unknown as TextureScene;
    makePlateTexture(target);
    makeCrackTexture(target);
    makeCoreTexture(target);
    makeDustTexture(target);
  }

  update(
    planets: readonly PlanetDrawState[],
    events: readonly PlanetDrawEvent[],
    nowMs: number,
    worldUnits: (mu: number) => number,
  ): void {
    const alive = new Set<string>();

    for (const planet of planets) {
      const centreX = worldUnits(planet.x);
      const centreY = worldUnits(planet.y);
      const radius = worldUnits(planet.radiusMu);

      /*
       * **A luz interna, por baixo de tudo.**
       *
       * A opacidade sobe com as placas que faltam — nada de bloom e nada de
       * pós-processamento: um sprite aditivo cuja transparência é uma função de
       * um facto autoritativo.
       */
      let standing = 0;
      for (let index = 0; index < planet.parts; index += 1) {
        if ((planet.mask & (1 << index)) !== 0) {
          standing += 1;
        }
      }
      const opened = planet.parts === 0 ? 0 : 1 - standing / planet.parts;
      const crackKey = `${planet.id}:${planet.generation}`;
      alive.add(crackKey);
      this.crackAt(crackKey)
        .setVisible(opened > 0.05)
        .setPosition(centreX, centreY)
        .setDisplaySize(radius * 1.5, radius * 1.5)
        .setAlpha(Math.min(0.7, opened * 0.75))
        .setTint(CRACK_TINT);

      for (let index = 0; index < planet.parts; index += 1) {
        if ((planet.mask & (1 << index)) === 0) {
          continue;
        }
        const key = `${planet.id}:${planet.generation}:${index}`;
        alive.add(key);
        const turn =
          ((planet.visualSeed % 360) + index * Math.floor(360 / Math.max(1, planet.parts))) % 360;
        const radians = (turn * Math.PI) / 180;
        const ring = index % 2 === 0 ? radius / 2 : (radius * 3) / 4;
        this.plateAt(key)
          .setVisible(true)
          .setPosition(centreX + Math.cos(radians) * ring, centreY + Math.sin(radians) * ring)
          .setDisplaySize(radius * 0.66, radius * 0.66)
          .setRotation(unit(planet.visualSeed, index) * Math.PI * 2)
          .setTint(PLATE_TINTS[hash(planet.visualSeed, index) % PLATE_TINTS.length] ?? 0x5a4f45);
      }

      /*
       * **Os núcleos EXPOSTOS, cada um no seu sítio.**
       *
       * A mesma aritmética que a sala usa para decidir qual deles um golpe
       * acerta — o que se vê e o que se acerta têm de ser o mesmo ponto.
       */
      for (const core of planet.cores) {
        const key = `${planet.id}:${planet.generation}:core:${core.index}`;
        alive.add(key);
        const offset = coreOffset(core.index, radius);
        const pulse = 0.8 + Math.sin(nowMs / 320 + core.index) * 0.14;
        this.coreAt(key)
          .setVisible(true)
          .setPosition(centreX + offset.dx, centreY + offset.dy)
          .setDisplaySize(radius * 0.34 * pulse, radius * 0.34 * pulse)
          .setAlpha(0.6 + (core.health / 1000) * 0.35)
          .setTint(core.health > 200 ? CORE_TINT : CORE_HALO_TINT);
      }
    }

    for (const [key, sprite] of this.plates) {
      if (!alive.has(key)) {
        sprite.setVisible(false);
      }
    }
    for (const [key, sprite] of this.cores) {
      if (!alive.has(key)) {
        sprite.setVisible(false);
      }
    }
    for (const [key, sprite] of this.cracks) {
      if (!alive.has(key)) {
        sprite.setVisible(false);
      }
    }

    for (const event of events) {
      const last = this.seenEvent.get(event.id) ?? 0;
      if (event.ordinal <= last) {
        continue;
      }
      this.seenEvent.set(event.id, event.ordinal);
      const x = worldUnits(event.x);
      const y = worldUnits(event.y);
      switch (event.kind) {
        case 'impact':
          this.flash(x, y, nowMs, 0.6, CRACK_TINT);
          this.puff(x, y, nowMs, event.ordinal, 2);
          break;
        case 'fragment':
          this.flash(x, y, nowMs, 1, CRACK_TINT);
          this.burst(x, y, nowMs, event.ordinal);
          this.puff(x, y, nowMs, event.ordinal, 4);
          break;
        case 'gold':
          /* O ouro, e só agora: a placa já não existe quando ele aparece. */
          this.flash(x, y, nowMs, 1.6, GOLD_TINT);
          break;
        case 'reveal':
          this.flash(x, y, nowMs, 1.8, CORE_HALO_TINT);
          break;
        case 'acquired':
          this.flash(x, y, nowMs, 2.2, CORE_TINT);
          break;
        case 'full':
          this.flash(x, y, nowMs, 1.2, CORE_HALO_TINT);
          break;
        case 'rebuilt':
          /*
           * A reconstrução não finge recompensa: um clarão frio e discreto, e o
           * corpo volta pelo estado autoritativo que a mesma trama trouxe.
           */
          this.flash(x, y, nowMs, 1.4, CRACK_TINT);
          break;
      }
    }

    this.advance(this.debris, nowMs, DEBRIS_LIFE_MS);
    this.advance(this.dust, nowMs, DUST_LIFE_MS);
    this.advance(this.flashes, nowMs, FLASH_LIFE_MS);
  }

  destroy(): void {
    for (const sprite of this.plates.values()) {
      sprite.destroy();
    }
    this.plates.clear();
    for (const sprite of this.cracks.values()) {
      sprite.destroy();
    }
    this.cracks.clear();
    for (const sprite of this.cores.values()) {
      sprite.destroy();
    }
    this.cores.clear();
    for (const pool of [this.debris, this.dust, this.flashes]) {
      for (const entry of pool) {
        entry.sprite.destroy();
      }
      pool.length = 0;
    }
    this.seenEvent.clear();
  }

  private plateAt(key: string): Phaser.GameObjects.Image {
    const existing = this.plates.get(key);
    if (existing !== undefined) {
      return existing;
    }
    const sprite = this.scene.add.image(0, 0, TEXTURE.plate).setDepth(this.depth).setVisible(false);
    this.plates.set(key, sprite);
    return sprite;
  }

  private crackAt(key: string): Phaser.GameObjects.Image {
    const existing = this.cracks.get(key);
    if (existing !== undefined) {
      return existing;
    }
    const sprite = this.scene.add
      .image(0, 0, TEXTURE.crack)
      .setDepth(this.depth - 1)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setVisible(false);
    this.cracks.set(key, sprite);
    return sprite;
  }

  private coreAt(key: string): Phaser.GameObjects.Image {
    const existing = this.cores.get(key);
    if (existing !== undefined) {
      return existing;
    }
    const sprite = this.scene.add
      .image(0, 0, TEXTURE.core)
      .setDepth(this.depth + 1)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setVisible(false);
    this.cores.set(key, sprite);
    return sprite;
  }

  private flash(x: number, y: number, nowMs: number, scale: number, tint: number): void {
    const entry = this.take(this.flashes, TEXTURE.crack, DEBRIS_MAX, true);
    if (entry === null) {
      return;
    }
    entry.x = x;
    entry.y = y;
    entry.vx = 0;
    entry.vy = 0;
    entry.bornAtMs = nowMs;
    entry.life = 1;
    entry.sprite
      .setVisible(true)
      .setPosition(x, y)
      .setDisplaySize(26 * scale, 26 * scale)
      .setTint(tint);
  }

  /** Fragmentos direcionais e limitados. Quatro por placa partida. */
  private burst(x: number, y: number, nowMs: number, ordinal: number): void {
    for (let index = 0; index < 4; index += 1) {
      const entry = this.take(this.debris, TEXTURE.plate, DEBRIS_MAX, false);
      if (entry === null) {
        return;
      }
      const angle = unit(ordinal, index) * Math.PI * 2;
      const speed = 0.06 + unit(ordinal, index + 71) * 0.07;
      entry.x = x;
      entry.y = y;
      entry.vx = Math.cos(angle) * speed;
      entry.vy = Math.sin(angle) * speed;
      entry.bornAtMs = nowMs;
      entry.life = 1;
      entry.sprite
        .setVisible(true)
        .setPosition(x, y)
        .setDisplaySize(7, 7)
        .setRotation(angle)
        .setTint(PLATE_TINTS[hash(ordinal, index) % PLATE_TINTS.length] ?? 0x5a4f45);
    }
  }

  /** Poeira: o primeiro corte do LOD, e o que menos custa perder. */
  private puff(x: number, y: number, nowMs: number, ordinal: number, count: number): void {
    for (let index = 0; index < count; index += 1) {
      const entry = this.take(this.dust, TEXTURE.dust, DUST_MAX, false);
      if (entry === null) {
        return;
      }
      const angle = unit(ordinal, index + 211) * Math.PI * 2;
      const speed = 0.02 + unit(ordinal, index + 307) * 0.03;
      entry.x = x;
      entry.y = y;
      entry.vx = Math.cos(angle) * speed;
      entry.vy = Math.sin(angle) * speed;
      entry.bornAtMs = nowMs;
      entry.life = 1;
      entry.sprite.setVisible(true).setPosition(x, y).setDisplaySize(4, 4).setTint(0x7a6d60);
    }
  }

  /**
   * Um lugar livre na piscina, ou um novo até ao teto.
   *
   * `null` quando o teto foi atingido, e é aí que o LOD age: o enfeite seguinte
   * é largado. Nunca uma placa, nunca um núcleo, nunca uma fenda e nunca um
   * impacto confirmado — esses desenham-se sempre, porque são informação.
   */
  private take(pool: Mote[], texture: string, ceiling: number, additive: boolean): Mote | null {
    for (const entry of pool) {
      if (entry.life <= 0) {
        return entry;
      }
    }
    if (pool.length >= ceiling) {
      return null;
    }
    const sprite = this.scene.add
      .image(0, 0, texture)
      .setDepth(this.depth + 2)
      .setBlendMode(additive ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL)
      .setVisible(false);
    const entry: Mote = { sprite, x: 0, y: 0, vx: 0, vy: 0, bornAtMs: 0, life: 0 };
    pool.push(entry);
    return entry;
  }

  private advance(pool: Mote[], nowMs: number, lifeMs: number): void {
    for (const entry of pool) {
      if (entry.life <= 0) {
        continue;
      }
      const age = nowMs - entry.bornAtMs;
      if (age >= lifeMs) {
        entry.life = 0;
        entry.sprite.setVisible(false);
        continue;
      }
      entry.x += entry.vx * 16;
      entry.y += entry.vy * 16;
      entry.sprite.setPosition(entry.x, entry.y).setAlpha(1 - age / lifeMs);
    }
  }
}

/**
 * Onde um núcleo é desenhado, face ao centro.
 *
 * A mesma aritmética que `openWorldPlanetCoreOffset` usa do lado autoritativo.
 * Duplicada aqui de propósito: o pacote do domínio trabalha em milli-unidades e
 * a cena em unidades de mundo, e importar a função obrigaria a converter duas
 * vezes. O que tem de coincidir é a FORMA, e ela está escrita nos dois sítios
 * com a mesma frase.
 */
function coreOffset(index: number, radius: number): { readonly dx: number; readonly dy: number } {
  if (index === 0) {
    return { dx: 0, dy: 0 };
  }
  const radians = ((index - 1) * Math.PI * 2) / 2 + Math.PI / 4;
  const ring = radius / 4;
  return { dx: Math.cos(radians) * ring, dy: Math.sin(radians) * ring };
}
